import { useMemo, useState } from 'react';
import { useStyles } from './styles';

import { createShareMessage, getShareImage, shareStyle } from 'utils/share';
import { ReactComponent as BtnDislikeFill } from '../../assets/svg/disliked_old.svg';
import { ReactComponent as BtnDislike } from '../../assets/svg/dislike_old.svg';
import { ReactComponent as BtnLikeFill } from '../../assets/svg/like_fill_v2.svg';
import { ReactComponent as BtnLike } from '../../assets/svg/like_v2.svg';
import { ReactComponent as BtnShare } from '../../assets/svg/share_v2.svg';
import { ReactComponent as BtnInfo } from '../../assets/svg/info_black.svg';
import { ReactComponent as MagicWhite } from '../../assets/svg/ai-magic-white.svg';
import { ReactComponent as MagicBlack } from '../../assets/svg/ai-magic-black.svg';
import { ReactionType, STRINGS } from '../../constants';
import { useAuthorize } from 'hooks/useAuthorize';
import { TOGGLE_REACTION } from 'graph_ql/reaction/mutations';
import { FEEDBACK_TRIGGER_TYPE } from 'component/style_item/type';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_FEEDBACK_OPTIONS } from 'graph_ql/feedback_options/queries';
import { FeedbackReason } from 'graph_ql/feedback/types';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import UserType from 'constants/user';
import MultiSelectPopup from 'component/MultiSelectPopup';
import { DEFAULT_FEEDBACK_OPTION, Options } from 'component/MultiSelectPopup/types';
import { CREATE_FEEDBACK } from 'graph_ql/feedback/mutations';
import { GET_REACTION_INFORMATION } from 'graph_ql/reaction/queries';
import BottomShareModal from 'component/share/share_bottom_modal';
import utils from 'utils/utils';
import { PATH } from 'routes/constant';
import styleItemUtils from 'utils/style_item';
import {
    getConversation_conversations_data_messages,
    getConversation_conversations_data_messages_elements,
} from '__generated__/getConversation';
import { ProductTypeEnum } from '__generated__/globalTypes';
import useUnlockExpProcessing from './hooks/useUnlockExpProcessing';
import { ENABLED_SHOW_MORE_STYLES } from './index';
import { useChatContext } from './hooks/ChatContext';
import { IMAGE_POPUP_TEXT_ACTION, TRACK_EVENT } from '../../utils/analytics';
import useEnableVtonFeature from '../../hooks/useEnableVtonFeature';

const PRODUCT_TYPE_PATH_MAPPING = {
    [ProductTypeEnum.item]: PATH.ITEM_DETAIL,
    [ProductTypeEnum.style]: PATH.STYLE_DETAIL,
};
const PRODUCT_TYPE_TITLE_MAPPING = {
    [ProductTypeEnum.item]: 'Item',
    [ProductTypeEnum.style]: 'Style',
};
enum ACTION_TYPE {
    SHARE = 'share',
    SHOW_INFO = 'show_info',
    TRY_ON = 'try_on',
}
interface IStyleActionProps {
    message: getConversation_conversations_data_messages;
    element: getConversation_conversations_data_messages_elements;
    openedInfoMessage?: string | null;
    setOpenedInfoMessage?: (state: string | null) => void;
    vtonSliderPercentage?: number | null;
    setVtonSliderPercentage?: (any) => void;
}
const StyleAction: React.FC<IStyleActionProps> = (props) => {
    const { openAuthPopup } = useUnlockExpProcessing();
    const {
        message,
        element: productDetail,
        openedInfoMessage,
        setOpenedInfoMessage,
        vtonSliderPercentage,
        setVtonSliderPercentage,
    } = props;
    const {
        handleScroll,
        expandedMessageTaffiIds,
        handleAnalytics,
        conversation,
        selectedElement,
        addExpandedMessageTaffiId,
    } = useChatContext();
    const enableVtonFeature = useEnableVtonFeature();
    const classes = useStyles();
    const authorize = useAuthorize();
    const userDetail = useSelector((state: RootState) => state.userDetail.me);
    const [feedbackOptions, setFeedbackOptions] = useState<FeedbackReason[]>([]);
    const [feedbackPopupShow, setFeedbackPopupShow] = useState<boolean>(false);
    const [customValueError, setCustomValueError] = useState<boolean>(false);
    const [sharing, setSharing] = useState(false);
    const [shareImage, setShareImage] = useState<File | null>(null);
    const [gettingShareImage, setGettingShareImage] = useState(false);
    const [reaction, setReaction] = useState({ like: false, dislike: false, totalLike: 0, totalDislike: 0 });
    const isCustomer = Boolean(userDetail && userDetail.userType === UserType.Customer);
    const [selectedFeedbacks, setSelectedFeedbacks] = useState<Options>(DEFAULT_FEEDBACK_OPTION);
    const storeSourceUrl = useSelector((state: RootState) => state.widget?.sourceUrl);
    const url = useMemo(() => {
        if ((utils.isAppInWidgetMode() && storeSourceUrl?.length > 0) || !utils.isAppInWidgetMode()) {
            return utils.getShareEntityUrl(
                PRODUCT_TYPE_PATH_MAPPING[productDetail?.productType as string],
                productDetail?.productTaffiId as string,
            );
        }
        return null;
    }, [productDetail?.productTaffiId, productDetail?.productType, storeSourceUrl]);
    const actions: any[] = [
        {
            action: ACTION_TYPE.SHARE,
            component: BtnShare,
            onClick: () => {
                setSharing(true);
                getShareImage(productDetail.value as string, setGettingShareImage, setShareImage);
            },
            isHidden: !url?.length,
        },
        {
            action: ACTION_TYPE.SHOW_INFO,
            component: BtnInfo,
            onClick: () => {
                if (setOpenedInfoMessage) {
                    if (message && message.taffiId) {
                        setOpenedInfoMessage(message.taffiId === openedInfoMessage ? null : message.taffiId);
                        if (!expandedMessageTaffiIds?.includes(message.taffiId)) {
                            addExpandedMessageTaffiId(message.taffiId);
                            handleAnalytics(
                                TRACK_EVENT.STYLE_CHAT_DESCRIPTION_ACTION,
                                {
                                    ...message,
                                    conversationId: message?.conversationId || conversation?.id,
                                    actionType: IMAGE_POPUP_TEXT_ACTION.MORE,
                                },
                                selectedElement,
                            );
                        }
                    }
                }
                setTimeout(() => {
                    handleScroll();
                }, 500);
            },
            isHidden: ENABLED_SHOW_MORE_STYLES,
        },
    ];
    if (enableVtonFeature) {
        actions.push({
            action: ACTION_TYPE.TRY_ON,
            component:
                !vtonSliderPercentage || (vtonSliderPercentage && vtonSliderPercentage < 50) ? MagicBlack : MagicWhite,
            onClick: () => {
                // toggle try on slider between 0 and 99
                if (setVtonSliderPercentage) {
                    setVtonSliderPercentage((prev: number) => (prev === 0 ? 99 : 0));
                }
            },
        });
    }
    actions.push(
        ...[
            {
                action: ReactionType.Dislike,
                component: reaction?.dislike ? BtnDislikeFill : BtnDislike,
            },
            {
                action: ReactionType.Like,
                component: reaction.like ? BtnLikeFill : BtnLike,
            },
        ],
    );
    const title = `Taffi ${PRODUCT_TYPE_TITLE_MAPPING[productDetail.productType as string]}: ${productDetail.label}`;

    const [toggleReactionMutation] = useMutation(TOGGLE_REACTION, {
        ...utils.gqlMutationOption({}),
        context: { allowAnonymousAuth: utils.isLocalUser() },
    });

    const handleOnCompleted = (response) => {
        setFeedbackOptions(response?.getFeedbackOptions);
    };

    const [getFeedbackOptions, { loading: getFeedbackOptionsLoading }] = useLazyQuery(
        GET_FEEDBACK_OPTIONS,
        utils.gqlQueryOption({
            fetchPolicy: 'network-only',
            onCompleted: handleOnCompleted,
        }),
    );

    const [createFeedback, { loading: createFeedbackLoading }] = useMutation(CREATE_FEEDBACK);

    const defaultApiVariables = useMemo(() => {
        return {
            collectionName: productDetail.collectionName,
            docId: productDetail?.productId,
        };
    }, [productDetail]);

    const openFeedBack = () => {
        setFeedbackPopupShow(true);
    };

    useQuery(GET_REACTION_INFORMATION, {
        ...utils.gqlQueryOption({
            variables: { ...defaultApiVariables },
            fetchPolicy: 'cache-and-network',
            skip: !defaultApiVariables.docId,
            onCompleted: (response) =>
                styleItemUtils.updateReactionFromResp(response?.getReactionInformation, setReaction),
        }),
        context: { allowAnonymousAuth: utils.isLocalUser() },
    });

    const handleCustomValueError = (state: boolean) => {
        setCustomValueError(state);
    };

    const submitFeedback = async () => {
        styleItemUtils.handleSubmitFeedback({
            selectedFeedbacks,
            handleCustomValueError,
            createFeedback,
            productDetail: productDetail as any,
        });
    };

    const handleCloseMultiSelectPopup = () => {
        setFeedbackPopupShow(false);
    };

    const customAuthorize = () => {
        openAuthPopup();
    };

    const handleReactionClick = async (action: ReactionType) => {
        const customAnalyticsData = {
            recommendedBy: STRINGS.STYLE_ASSISTANT,
        };
        styleItemUtils.handleReaction({
            authorize,
            customAuthorize,
            userDetail,
            productDetail: productDetail as any,
            customAnalyticsData,
            action,
            isCustomer,
            reaction,
            setReaction,
            toggleReactionMutation,
            getFeedbackOptions,
            openFeedBack,
            feedbackType: FEEDBACK_TRIGGER_TYPE.DISLIKE,
        });
    };
    return (
        <>
            <div className={classes.styleActionContainer}>
                {actions
                    .filter((action) => !action?.isHidden)
                    .map((item, index) => {
                        return (
                            <div
                                key={index}
                                className={classes.styleAction}
                                style={{
                                    marginLeft: index && '4px',
                                    backgroundColor:
                                        item?.action === ACTION_TYPE.SHOW_INFO && openedInfoMessage === message.taffiId
                                            ? '#F2D4BD'
                                            : 'transparent',
                                }}
                                onClick={() => {
                                    if (item?.onClick) {
                                        item.onClick();
                                    } else {
                                        handleReactionClick(item.action);
                                    }
                                }}
                            >
                                <item.component className={classes.actionIcon} />
                            </div>
                        );
                    })}
            </div>
            <MultiSelectPopup
                show={feedbackPopupShow}
                onClose={handleCloseMultiSelectPopup}
                title={STRINGS.ADD_FEEDBACK}
                options={feedbackOptions}
                selectedOptions={selectedFeedbacks}
                setSelectedOptions={setSelectedFeedbacks}
                loading={getFeedbackOptionsLoading}
                handleSubmit={submitFeedback}
                customValueError={customValueError}
                setCustomValueError={setCustomValueError}
                setHidePopup={setFeedbackPopupShow}
                successMessage={STRINGS.FEEDBACK_RECEIVED}
                successDetails={STRINGS.FEEDBACK_SUCCESS_MESSAGE}
                submitLoading={createFeedbackLoading}
            />
            <BottomShareModal
                text={createShareMessage(productDetail.productType as string, url!, userDetail)}
                subject={title}
                open={sharing}
                onClose={() => {
                    setSharing(false);
                    setShareImage(null);
                }}
                onMoreSelect={() => {
                    return shareStyle(title, productDetail.productType as string, url!, userDetail, shareImage);
                }}
                link={url!}
                moreLoading={gettingShareImage}
            />
        </>
    );
};

export default StyleAction;
